import { Check, ChevronsUpDown, Plus, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

import { Badge } from './badge.tsx'
import { Button } from './button.tsx'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from './command.tsx'
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx'

export type ComboboxOption = {
	value: string
	label: string
	keywords?: Array<string>
}

type ComboboxBaseProps = {
	options: ComboboxOption[]
	placeholder?: string
	searchPlaceholder?: string
	emptyMessage?: string
	disabled?: boolean
	className?: string
	triggerProps?: React.ComponentProps<typeof Button>
	contentProps?: React.ComponentProps<typeof PopoverContent>
	/** Allow creating new entries when not found */
	allowCreate?: boolean
	/** Callback when creating a new entry. Receives the search query and should return the new value */
	onCreate?: (searchQuery: string) => void | Promise<void>
	/** Custom label for the create button. Default: "Create" */
	createLabel?: string
	/**
	 * Called on every keystroke in the search box. Pair with
	 * `shouldFilter={false}` to drive the `options` from a server search
	 * instead of cmdk's client-side filtering.
	 */
	onSearchChange?: (query: string) => void
	/** Forwarded to cmdk. Set to `false` when `options` are already filtered. */
	shouldFilter?: boolean
}

export type ComboboxProps =
	| (ComboboxBaseProps & {
			/** Enable multi-select mode */
			multiple?: false
			value?: string
			defaultValue?: string
			onValueChange?: (value: string | undefined) => void
	  })
	| (ComboboxBaseProps & {
			/** Enable multi-select mode */
			multiple: true
			value?: string[]
			defaultValue?: string[]
			onValueChange?: (value: string[]) => void
	  })

export function Combobox(props: ComboboxProps) {
	const {
		options,
		value,
		defaultValue,
		onValueChange,
		placeholder = 'Select an option',
		searchPlaceholder = 'Search options...',
		emptyMessage = 'No option found.',
		disabled,
		className,
		triggerProps,
		contentProps,
		allowCreate = false,
		onCreate,
		createLabel = 'Create',
		onSearchChange,
		shouldFilter,
	} = props

	const multiple = props.multiple ?? false

	const [open, setOpen] = React.useState(false)
	const [searchQuery, setSearchQuery] = React.useState('')

	const isControlled = value !== undefined
	const [uncontrolledValue, setUncontrolledValue] = React.useState<
		string | string[] | undefined
	>(defaultValue)
	const selectedValue = isControlled ? value : uncontrolledValue
	const {
		className: triggerClassName,
		type,
		...restTriggerProps
	} = triggerProps ?? {}

	const handleSelect = (nextValue: string) => {
		if (multiple) {
			const currentValues = (selectedValue as string[] | undefined) ?? []
			const newValues = currentValues.includes(nextValue)
				? currentValues.filter((v) => v !== nextValue)
				: [...currentValues, nextValue]

			if (!isControlled) {
				setUncontrolledValue(newValues)
			}
			;(onValueChange as ((value: string[]) => void) | undefined)?.(newValues)
		} else {
			if (!isControlled) {
				setUncontrolledValue(nextValue)
			}
			;(onValueChange as ((value: string | undefined) => void) | undefined)?.(
				nextValue,
			)
			setOpen(false)
		}
	}

	const handleRemove = (valueToRemove: string) => {
		if (!multiple) return
		const currentValues = (selectedValue as string[] | undefined) ?? []
		const newValues = currentValues.filter((v) => v !== valueToRemove)

		if (!isControlled) {
			setUncontrolledValue(newValues)
		}
		;(onValueChange as ((value: string[]) => void) | undefined)?.(newValues)
	}

	const handleCreate = async () => {
		if (!searchQuery.trim() || !onCreate) return
		await onCreate(searchQuery.trim())
		setSearchQuery('')
		if (!multiple) {
			setOpen(false)
		}
	}

	const selectedOption = multiple
		? undefined
		: options.find((option) => option.value === selectedValue)

	const selectedOptions = multiple
		? options.filter((option) =>
				(selectedValue as string[] | undefined)?.includes(option.value),
			)
		: []

	// Check if search query matches any existing options
	const hasExactMatch = options.some(
		(option) =>
			option.label.toLowerCase() === searchQuery.toLowerCase() ||
			option.value.toLowerCase() === searchQuery.toLowerCase(),
	)
	const showCreateOption = allowCreate && searchQuery.trim() && !hasExactMatch

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					{...restTriggerProps}
					variant="outline"
					role="combobox"
					aria-expanded={open}
					className={cn(
						'border-input bg-background ring-offset-background hover:bg-background focus:ring-ring h-10 w-full justify-between rounded-md border px-3 py-2 text-base font-normal shadow-none focus:ring-2 focus:ring-offset-2 active:scale-100 md:text-sm',
						!selectedOption &&
							selectedOptions.length === 0 &&
							'text-muted-foreground',
						className,
						triggerClassName,
					)}
					disabled={disabled}
					type={type ?? 'button'}
				>
					{multiple ? (
						<div className="flex flex-1 flex-wrap gap-1">
							{selectedOptions.length > 0 ? (
								selectedOptions.map((option) => (
									<Badge
										key={option.value}
										variant="secondary"
										className="mr-1"
									>
										{option.label}
										<span
											role="button"
											tabIndex={0}
											className="ring-offset-background focus:ring-ring ml-1 cursor-pointer rounded-sm outline-none focus:ring-2 focus:ring-offset-2"
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault()
													e.stopPropagation()
													handleRemove(option.value)
												}
											}}
											onMouseDown={(e) => {
												e.preventDefault()
												e.stopPropagation()
											}}
											onClick={(e) => {
												e.preventDefault()
												e.stopPropagation()
												handleRemove(option.value)
											}}
											aria-label={`Remove ${option.label}`}
										>
											<X className="text-muted-foreground hover:text-foreground h-3 w-3" />
										</span>
									</Badge>
								))
							) : (
								<span className="text-muted-foreground">{placeholder}</span>
							)}
						</div>
					) : (
						(selectedOption?.label ?? placeholder)
					)}
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				{...(contentProps ?? {})}
				align={contentProps?.align ?? 'start'}
				className={cn(
					'w-[var(--radix-popover-trigger-width)] p-0',
					contentProps?.className,
				)}
			>
				<Command shouldFilter={shouldFilter}>
					<CommandInput
						placeholder={searchPlaceholder}
						onValueChange={(query) => {
							setSearchQuery(query)
							onSearchChange?.(query)
						}}
					/>
					<CommandList>
						<CommandEmpty>
							{showCreateOption ? null : emptyMessage}
						</CommandEmpty>
						<CommandGroup>
							{options.map((option) => {
								const isSelected = multiple
									? ((selectedValue as string[] | undefined)?.includes(
											option.value,
										) ?? false)
									: option.value === selectedValue

								return (
									<CommandItem
										key={option.value}
										value={[
											option.label,
											option.value,
											...(option.keywords ?? []),
										].join(' ')}
										onSelect={() => handleSelect(option.value)}
									>
										<Check
											className={cn(
												'mr-2 h-4 w-4',
												isSelected ? 'opacity-100' : 'opacity-0',
											)}
										/>
										{option.label}
									</CommandItem>
								)
							})}
							{showCreateOption && (
								<CommandItem
									value={searchQuery}
									keywords={[createLabel, 'create', 'new', 'add']}
									onSelect={handleCreate}
									className="text-primary cursor-pointer"
								>
									<span className="border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border">
										<Plus className="h-3 w-3" aria-hidden="true" />
									</span>
									{createLabel} "{searchQuery}"
								</CommandItem>
							)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}
